import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet, apiPost, streamNdjson } from "../api";
import { useAuth } from "../auth/AuthContext";
import { CardHover, CardThumb } from "../components/CardImage";
import { CardPicker } from "../components/CardPicker";
import { Skeleton, Spinner } from "../components/Loaders";
import { NumberStepper } from "../components/NumberStepper";
import { Select } from "../components/Select";
import { PageIntro } from "../components/Visual";
import { Icon } from "../components/Icons";
import { ProAuditView, type ProAuditStats } from "./Analyze";
import type {
  CardSearchHit,
  CollectionListResponse,
  CollectionSummary,
  FormatMeta,
  QueueStatus,
  SavedDeck,
  SavedDeckSummary,
  SetMeta,
} from "../types";

// ---------------------------------------------------------------------------
// Constants (ported from legacy app.js)
// ---------------------------------------------------------------------------

const BUDGET_PRESETS: Record<
  string,
  { rares: number | ""; mythics: number | "" }
> = {
  unlimited: { rares: "", mythics: "" },
  budget: { rares: 16, mythics: 4 },
  low: { rares: 8, mythics: 2 },
  verylow: { rares: 4, mythics: 1 },
  none: { rares: 0, mythics: 0 },
};

interface ArenaQuestSuggestion {
  quest: string;
  prompt: string;
}

const ARENA_QUEST_SUGGESTIONS: ArenaQuestSuggestion[] = [
  { quest: "Attack with 45 creatures.", prompt: "Build an aggressive creature deck that attacks early and often." },
  { quest: "Cast 40 creature spells.", prompt: "Build a creature-heavy deck with many cheap creatures." },
  { quest: "Kill 25 creatures.", prompt: "Build an interactive removal deck with plenty of kill spells and combat pressure." },
  { quest: "Play 40 lands.", prompt: "Build a deck that naturally plays lands over longer games, with ramp, card draw, or landfall synergies." },
  { quest: "Cast 30 white or blue spells.", prompt: "Build a white-blue deck with many castable white and blue spells." },
  { quest: "Cast 30 blue or black spells.", prompt: "Build a blue-black deck with many castable blue and black spells." },
  { quest: "Cast 30 black or red spells.", prompt: "Build a black-red deck with many castable black and red spells." },
  { quest: "Cast 30 red or green spells.", prompt: "Build a red-green deck with many castable red and green spells." },
  { quest: "Cast 30 green or white spells.", prompt: "Build a green-white deck with many castable green and white spells." },
  { quest: "Cast 30 white or black spells.", prompt: "Build a white-black deck with many castable white and black spells." },
  { quest: "Cast 30 blue or red spells.", prompt: "Build a blue-red deck with many castable blue and red spells." },
  { quest: "Cast 30 black or green spells.", prompt: "Build a black-green deck with many castable black and green spells." },
  { quest: "Cast 30 red or white spells.", prompt: "Build a red-white deck with many castable red and white spells." },
  { quest: "Cast 30 green or blue spells.", prompt: "Build a green-blue deck with many castable green and blue spells." },
  { quest: "Attack with 30 creatures.", prompt: "Build an aggressive creature deck that attacks early and often." },
  { quest: "Cast 20 creature spells.", prompt: "Build a creature-heavy deck with many cheap creatures." },
  { quest: "Kill 15 creatures.", prompt: "Build an interactive removal deck with plenty of kill spells and combat pressure." },
  { quest: "Play 25 lands.", prompt: "Build a deck that naturally plays lands over longer games, with ramp, card draw, or landfall synergies." },
  { quest: "Cast 20 white or blue spells.", prompt: "Build a white-blue deck with many castable white and blue spells." },
  { quest: "Cast 20 blue or black spells.", prompt: "Build a blue-black deck with many castable blue and black spells." },
  { quest: "Cast 20 black or red spells.", prompt: "Build a black-red deck with many castable black and red spells." },
  { quest: "Cast 20 red or green spells.", prompt: "Build a red-green deck with many castable red and green spells." },
  { quest: "Cast 20 green or white spells.", prompt: "Build a green-white deck with many castable green and white spells." },
  { quest: "Cast 20 white or black spells.", prompt: "Build a white-black deck with many castable white and black spells." },
  { quest: "Cast 20 blue or red spells.", prompt: "Build a blue-red deck with many castable blue and red spells." },
  { quest: "Cast 20 black or green spells.", prompt: "Build a black-green deck with many castable black and green spells." },
  { quest: "Cast 20 red or white spells.", prompt: "Build a red-white deck with many castable red and white spells." },
  { quest: "Cast 20 blue or green spells.", prompt: "Build a blue-green deck with many castable blue and green spells." },
];

const PHASE_LABELS: Record<string, string> = {
  theme: "Extracting deck theme",
  pool: "Retrieving candidate cards",
  assemble: "Assembling deck with LLM",
  validate: "Validating and repairing",
  scenarios: "Writing play guide",
};

type ProgressKind = "active" | "done" | "detail" | "error";
interface ProgressItem { text: string; kind: ProgressKind }

interface DeckEntry {
  name: string;
  count: number;
  card?: {
    mana_cost?: string;
    type_line?: string;
    cmc?: number;
    image_url?: string | null;
  };
}

interface DeckShape {
  mainboard?: DeckEntry[];
  sideboard?: DeckEntry[];
  commander?: DeckEntry[];
  companion?: DeckEntry[];
}

/** In-app error for collection pool too tight; shows recovery actions. */
function BuildFloodErrorPanel({
  error: message,
  onContinueAllowLandFlood,
  onContinueOutsideCollection,
}: {
  error: string;
  onContinueAllowLandFlood: () => void;
  onContinueOutsideCollection: () => void;
}) {
  return (
    <div className="error-banner error-banner--stack">
      <p className="error-banner__message">{message}</p>
      <div className="error-banner__row">
        <button type="button" className="primary" onClick={onContinueOutsideCollection}>
          Complete with cards outside my collection
        </button>
        <button type="button" className="primary" onClick={onContinueAllowLandFlood}>
          Continue anyway
        </button>
      </div>
      <p className="hint error-banner__hint">
        Completing with outside cards keeps the collection-built deck and only
        adds the missing non-owned cards. Continue anyway still enforces your
        collection: it finalizes the best list we could make, even if the main
        is short of 60 or the land count is over the safe cap.
      </p>
    </div>
  );
}

interface Scenarios {
  opening_hand?: string;
  key_interactions?: string[];
  game_plan?: { phase: string; plan: string }[];
  matchups?: { against: string; strategy: string }[];
  win_conditions?: string[];
}

interface BuildResult {
  type: "done";
  name?: string;
  deck: DeckShape;
  totals?: { mainboard: number; sideboard: number };
  rarity_counts?: Record<string, number>;
  caps?: { max_rares?: number; max_mythics?: number };
  theme?: {
    archetype?: string;
    colors?: string[];
    themes?: string[];
    notes?: string;
    resolved_sets?: { code: string; name: string }[];
    land_count?: number;
  };
  sets?: { code: string; name: string }[];
  set_conformance?: { in_set: number; total_non_basic: number; ratio: number };
  collection_build?: {
    constrained: boolean;
    owned_unique_in_pool: number;
    owned_unique_used: number;
    owned_copies_used: number;
    outside_collection_unique?: number;
    outside_collection_copies?: number;
    extra_basics_added: number;
    quality: "good" | "tight" | "poor";
    message: string;
  } | null;
  rationale?: string;
  mission?: string;
  arena_text?: string;
  scenarios?: Scenarios;
  notes?: string[];
  pro_audit?: ProAuditStats;
}

// ---------------------------------------------------------------------------

function BuildSwitchRow({
  labelText,
  checked,
  onChange,
  disabled,
  children,
  tip,
}: {
  labelText: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  children: ReactNode;
  tip?: string;
}) {
  const id = useId();
  const toggle = () => {
    if (!disabled) onChange(!checked);
  };

  return (
    <div
      className={`build-option${disabled ? " is-disabled" : ""}`}
      title={tip}
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled || undefined}
      aria-labelledby={`${id}-text`}
      tabIndex={disabled ? -1 : 0}
      onClick={(event) => {
        const target = event.target;
        if (target instanceof Element && target.closest("a, button, input, select, textarea, label")) return;
        toggle();
      }}
      onKeyDown={(event) => {
        if (event.key !== " " && event.key !== "Enter") return;
        event.preventDefault();
        toggle();
      }}
    >
      <div className="build-option__text" id={`${id}-text`}>
        <span className="build-option__label">{labelText}</span>
        <div className="build-option__desc">{children}</div>
      </div>
      <label className="build-switch">
        <input
          type="checkbox"
          role="switch"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          aria-hidden="true"
          tabIndex={-1}
        />
        <span className="build-switch__track" />
      </label>
    </div>
  );
}

export function Build() {
  const { profile, refreshProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const publicDeckId = searchParams.get("publicDeck");

  // Form state
  const [formats, setFormats] = useState<FormatMeta[]>([]);
  const [format, setFormat] = useState("standard");
  const [query, setQuery] = useState("");
  const [includeSideboard, setIncludeSideboard] = useState(false);
  const [arenaOnly, setArenaOnly] = useState(true);

  // Collection gate: only usable when the user has imported + saved their
  // Arena collection on the Collection page.
  const [collectionOnly, setCollectionOnly] = useState(false);
  const [savedCollection, setSavedCollection] = useState<{
    saved: boolean;
    uniqueCards: number;
    totalCards: number;
    collectionId: string | null;
  } | null>(null);
  const [collections, setCollections] = useState<CollectionSummary[]>([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);

  // Budget
  const [preset, setPreset] = useState("unlimited");
  const [maxRares, setMaxRares] = useState<string>("");
  const [maxMythics, setMaxMythics] = useState<string>("");

  // "Build around this card" picker - supports up to a handful of
  // centerpiece cards so users can seed the build with multiple synergistic
  // pieces (e.g. a combo payoff plus its enabler).
  const [aroundCards, setAroundCards] = useState<CardSearchHit[]>([]);
  const AROUND_CARDS_MAX = 5;

  // Sets picker
  const [availableSets, setAvailableSets] = useState<SetMeta[]>([]);
  const [setsInput, setSetsInput] = useState("");
  const [selectedSets, setSelectedSets] = useState<SetMeta[]>([]);
  const [onlySets, setOnlySets] = useState(false);

  // Stream state
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<ProgressItem[]>([]);
  const [result, setResult] = useState<BuildResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const abortRef = useRef<(() => void) | null>(null);
  const [loadedPublicDeckId, setLoadedPublicDeckId] = useState<string | null>(null);

  const [metaLoading, setMetaLoading] = useState(true);
  const [queueStatus, setQueueStatus] = useState<QueueStatus | null>(profile?.queue ?? null);

  useEffect(() => {
    setQueueStatus(profile?.queue ?? null);
  }, [profile?.queue]);

  // Load formats + sets on mount
  useEffect(() => {
    let cancelled = false;
    setMetaLoading(true);
    Promise.allSettled([
      apiGet<FormatMeta[]>("/api/formats"),
      apiGet<SetMeta[]>("/api/sets"),
    ]).then((results) => {
      if (cancelled) return;
      const [fRes, sRes] = results;
      if (fRes.status === "fulfilled") setFormats(fRes.value);
      if (sRes.status === "fulfilled") setAvailableSets(sRes.value);
      setMetaLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch the user's saved collection (if any) so the "only from my
  // collection" toggle can self-disable with a helpful message when there's
  // nothing to build from.
  useEffect(() => {
    let cancelled = false;
    apiGet<CollectionListResponse>("/api/collections")
      .then((data) => {
        if (cancelled) return;
        const all = data.collections || [];
        setCollections(all);
        const first = all[0];
        setSelectedCollectionId(first?.id ?? null);
        if (!first) {
          setSavedCollection({ saved: false, uniqueCards: 0, totalCards: 0, collectionId: null });
          return;
        }
        setSavedCollection({
          saved: true,
          uniqueCards: first.totals?.unique_cards ?? 0,
          totalCards: first.totals?.total_cards ?? 0,
          collectionId: first.id,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setSavedCollection({ saved: false, uniqueCards: 0, totalCards: 0, collectionId: null });
        setCollections([]);
        setSelectedCollectionId(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!collections.length) return;
    const selected =
      (selectedCollectionId && collections.find((c) => c.id === selectedCollectionId)) ||
      collections[0];
    if (!selected) return;
    if (!selectedCollectionId || selectedCollectionId !== selected.id) {
      setSelectedCollectionId(selected.id);
    }
    setSavedCollection({
      saved: true,
      uniqueCards: selected.totals?.unique_cards ?? 0,
      totalCards: selected.totals?.total_cards ?? 0,
      collectionId: selected.id,
    });
  }, [selectedCollectionId, collections]);

  useEffect(() => {
    if (!publicDeckId || loadedPublicDeckId === publicDeckId) return;
    let cancelled = false;
    setLoadedPublicDeckId(publicDeckId);
    apiGet<SavedDeck>(`/api/public/decks/${encodeURIComponent(publicDeckId)}`)
      .then((deck) => {
        if (cancelled) return;
        if (deck.format) setFormat(deck.format);
        const prompt = [
          `Build off the public deck "${deck.name}". Keep its core plan, colors, and strongest synergies, then tune it into a fresh list.`,
          deck.query ? `Original request: ${deck.query}` : "",
          deck.arena_text ? `Starting decklist:\n${deck.arena_text}` : "",
        ]
          .filter(Boolean)
          .join("\n\n");
        setQuery(prompt);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(`Could not load public deck: ${(e as Error).message}`);
          setErrorCode(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [publicDeckId, loadedPublicDeckId]);

  // If the user ticks the checkbox but has no saved collection, untick it
  // again so we don't submit a request that would just get warned about
  // server-side.
  useEffect(() => {
    if (collectionOnly && savedCollection && !savedCollection.saved) {
      setCollectionOnly(false);
    }
  }, [collectionOnly, savedCollection]);

  // When the user is building strictly from their owned cards, ownership
  // already caps rares/mythics far more tightly than any budget preset
  // could, so force the preset back to Unlimited and clear the manual
  // caps. Re-enables once they untick "only from my collection".
  const budgetLocked = collectionOnly && !!savedCollection?.saved;
  useEffect(() => {
    if (!budgetLocked) return;
    setPreset("unlimited");
    setMaxRares("");
    setMaxMythics("");
  }, [budgetLocked]);

  const setsByCode = useMemo(() => {
    const m = new Map<string, SetMeta>();
    for (const s of availableSets) m.set(s.code.toUpperCase(), s);
    return m;
  }, [availableSets]);
  const setsByName = useMemo(() => {
    const m = new Map<string, SetMeta>();
    for (const s of availableSets) if (s.name) m.set(s.name.toLowerCase(), s);
    return m;
  }, [availableSets]);

  const resolveSetInput = (raw: string): SetMeta | null => {
    const text = raw.trim();
    if (!text) return null;
    const paren = text.match(/\(([A-Za-z0-9]{2,6})\)\s*$/);
    if (paren) {
      const c = paren[1].toUpperCase();
      if (setsByCode.has(c)) return setsByCode.get(c)!;
    }
    const codeGuess = text.toUpperCase();
    if (setsByCode.has(codeGuess)) return setsByCode.get(codeGuess)!;
    const low = text.toLowerCase();
    if (setsByName.has(low)) return setsByName.get(low)!;
    return availableSets.find((s) => (s.name || "").toLowerCase().includes(low)) ?? null;
  };

  const addSet = () => {
    const matched = resolveSetInput(setsInput);
    if (!matched) {
      alert(`"${setsInput}" did not match any set. Pick one from the suggestions or use a 3-letter set code.`);
      return;
    }
    if (!selectedSets.some((s) => s.code === matched.code)) {
      setSelectedSets((xs) => [...xs, matched]);
    }
    setSetsInput("");
  };

  // Fires on every input change. When the new value matches a datalist
  // suggestion exactly (i.e. the user clicked one from the dropdown rather
  // than free-typing), auto-add that set so they don't also need to click
  // a separate "Add" button.
  const onSetsInputChange = (raw: string) => {
    setSetsInput(raw);
    const text = raw.trim();
    if (!text) return;
    // Datalist option values are formatted as "Name (CODE)"; an exact hit
    // only comes from a dropdown pick or a paste of the same string.
    const paren = text.match(/^(.+)\s+\(([A-Za-z0-9]{2,6})\)\s*$/);
    if (!paren) return;
    const code = paren[2].toUpperCase();
    const match = setsByCode.get(code);
    if (!match) return;
    // Only accept if the name portion also matches, to guard against the
    // user having typed a legitimate close string that happens to end in
    // a parenthesised code.
    if ((match.name || "").toLowerCase() !== paren[1].trim().toLowerCase()) return;
    if (!selectedSets.some((s) => s.code === match.code)) {
      setSelectedSets((xs) => [...xs, match]);
    }
    setSetsInput("");
  };

  const onPresetChange = (v: string) => {
    setPreset(v);
    if (v === "custom") return;
    const p = BUDGET_PRESETS[v];
    if (!p) return;
    setMaxRares(p.rares === "" ? "" : String(p.rares));
    setMaxMythics(p.mythics === "" ? "" : String(p.mythics));
  };

  const onManualBudget = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setPreset("custom");
  };

  const cancelBuild = () => {
    abortRef.current?.();
  };

  const appendProgress = (text: string, kind: ProgressKind = "detail") => {
    setProgress((p) => [...p, { text, kind }]);
  };
  const markPreviousDone = () => {
    setProgress((p) =>
      p.map((it) => (it.kind === "active" ? { ...it, kind: "done" } : it)),
    );
  };
  /** Stop the phase spinner when the build fails (stream error, HTTP, etc.). */
  const stallActivePhases = () => {
    setProgress((p) =>
      p.map((it) => (it.kind === "active" ? { ...it, kind: "detail" } : it)),
    );
  };

  const handleEvent = (ev: any) => {
    if (ev.type === "phase") {
      markPreviousDone();
      appendProgress(PHASE_LABELS[ev.phase] || ev.phase, "active");
    } else if (ev.type === "theme") {
      const t = ev.theme ?? {};
      appendProgress(
        `Theme: ${t.archetype || "?"} / ${(t.colors || []).join("") || "-"} / lands ${t.land_count ?? "?"}`,
        "detail",
      );
      if ((t.resolved_sets || []).length) {
        const label = (t.resolved_sets as { code: string; name: string }[])
          .map((s) => `${s.name} (${s.code})`)
          .join(", ");
        appendProgress(`Detected sets: ${label}`, "detail");
      }
    } else if (ev.type === "pool") {
      let line = `Candidate pool size: ${ev.size}`;
      if (ev.in_set_count != null && ev.sets && ev.sets.length) {
        line += ` (${ev.in_set_count} from featured sets)`;
      }
      appendProgress(line, "detail");
    } else if (ev.type === "done") {
      markPreviousDone();
      appendProgress("Done", "done");
      setResult(ev as BuildResult);
      refreshProfile();
    } else if (ev.type === "warning") {
      appendProgress(ev.message || "Warning", "detail");
    } else if (ev.type === "error") {
      stallActivePhases();
      const errMsg = (ev as { message?: string }).message || "Unknown error";
      setError(errMsg);
      setErrorCode((ev as { code?: string | null }).code ?? null);
    } else if (ev.type === "queue") {
      const snap = ev as QueueStatus;
      setQueueStatus(snap);
      if (snap.status === "queued" && snap.position) {
        appendProgress(`Queued: position ${snap.position} (${snap.waiting} waiting total)`, "detail");
      } else if (snap.status === "active") {
        appendProgress(`Queue slot acquired (${snap.active}/${snap.capacity} active)`, "detail");
      }
    }
  };

  const parseCap = (v: string): number | null => {
    if (!v) return null;
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
  };

  type BuildPostBody = {
    query: string;
    format: string;
    include_sideboard: boolean;
    arena_only: boolean;
    max_rares: number | null;
    max_mythics: number | null;
    prefer_sets: string[];
    only_sets: boolean;
    around_cards: string[];
    around_card: string | null;
    collection_only: boolean;
    collection_id: string | null;
    allow_collection_land_flood?: boolean;
    allow_off_collection_fill?: boolean;
  };

  const buildPostBody = (
    overrides: Partial<BuildPostBody> = {},
  ): BuildPostBody => {
    const base: BuildPostBody = {
      query: query.trim(),
      format,
      include_sideboard: includeSideboard,
      arena_only: arenaOnly,
      max_rares: parseCap(maxRares),
      max_mythics: parseCap(maxMythics),
      prefer_sets: selectedSets.map((s) => s.code),
      only_sets: onlySets && selectedSets.length > 0,
      around_cards: aroundCards.map((c) => c.name),
      // Legacy single-card field kept for backward compatibility. Older
      // backends ignored the list; newer backends prefer `around_cards`.
      around_card: aroundCards[0]?.name ?? null,
      collection_only: collectionOnly && !!savedCollection?.saved,
      collection_id: collectionOnly && !!savedCollection?.saved
        ? (selectedCollectionId ?? savedCollection?.collectionId ?? null)
        : null,
    };
    return { ...base, ...overrides };
  };

  const onBuild = () => {
    void runBuild();
  };

  const addQuestSuggestion = (suggestion: ArenaQuestSuggestion) => {
    setQuery((current) => {
      const trimmed = current.trim();
      if (!trimmed) return suggestion.prompt;
      if (trimmed.includes(suggestion.quest)) return current;
      return `${trimmed}\n\n${suggestion.prompt}`;
    });
  };

  const runBuild = async (overrides: Partial<BuildPostBody> = {}) => {
    const body = buildPostBody(overrides);
    setRunning(true);
    setError(null);
    setErrorCode(null);
    setProgress([]);
    setResult(null);

    const { promise, abort } = streamNdjson<any>({
      path: "/api/build",
      body,
      onEvent: handleEvent,
      onError: (msg) => {
        stallActivePhases();
        setError(msg);
        setErrorCode(null);
      },
    });
    abortRef.current = abort;
    try {
      await promise;
    } catch {
      /* already surfaced via onError or handleEvent */
    } finally {
      abortRef.current = null;
      setRunning(false);
      refreshProfile();
    }
  };

  const onContinueAllowLandFlood = () => {
    setError(null);
    setErrorCode(null);
    void runBuild({ allow_collection_land_flood: true });
  };

  const onContinueOutsideCollection = () => {
    setError(null);
    setErrorCode(null);
    void runBuild({
      collection_only: true,
      collection_id: selectedCollectionId ?? savedCollection?.collectionId ?? null,
      allow_off_collection_fill: true,
    });
  };

  const showFloodRecovery =
    !running &&
    !!error &&
    (errorCode === "collection_incomplete_flood" || error.includes("land-flood build"));

  const capWarning = profile?.plan === "free" && (profile.buildsRemaining ?? 0) <= 0;
  const remaining = profile?.buildsRemaining;
  const queueHint = queueStatus
    ? queueStatus.status === "queued" && queueStatus.position
      ? `In queue: position ${queueStatus.position} (${queueStatus.waiting} waiting, ${queueStatus.active}/${queueStatus.capacity} active).`
      : queueStatus.status === "active"
      ? `Now generating: ${queueStatus.active}/${queueStatus.capacity} active slot${queueStatus.capacity === 1 ? "" : "s"} in use.`
      : `Queue load: ${queueStatus.waiting} waiting, ${queueStatus.active}/${queueStatus.capacity} active.`
    : null;
  const selectedFormatLabel =
    formats.find((f) => f.id === format)?.label || format.toUpperCase();
  const selectedCollection = collections.find((c) => c.id === selectedCollectionId);
  const selectedCollectionLabel =
    selectedCollection?.name?.trim() ||
    (savedCollection?.saved ? "Saved collection" : "No collection");
  const selectedBudgetLabel =
    budgetLocked
      ? "Owned cards"
      : preset === "custom"
      ? "Custom caps"
      : preset === "unlimited"
      ? "Unlimited"
      : preset === "budget"
      ? "Budget"
      : preset === "low"
      ? "Low budget"
      : preset === "verylow"
      ? "Very low"
      : "No rares/mythics";
  const hasBrief =
    query.trim().length > 0 || aroundCards.length > 0 || selectedSets.length > 0;
  const showQueueInProgress =
    !result && (running || progress.length > 0);
  const showQueueInSidebar = !result && !running && progress.length === 0;
  const buildSummaryItems = [
    { label: "Format", value: selectedFormatLabel, icon: "cards" as const },
    { label: "Budget", value: selectedBudgetLabel, icon: "gauge" as const },
    {
      label: "Pool",
      value: collectionOnly && savedCollection?.saved ? selectedCollectionLabel : "Full legal card pool",
      icon: "archive" as const,
    },
    {
      label: "Seeds",
      value: aroundCards.length ? `${aroundCards.length} card${aroundCards.length === 1 ? "" : "s"}` : "Open theme",
      icon: "sparkles" as const,
    },
  ];

  useEffect(() => {
    if (!profile) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const snap = await apiGet<QueueStatus>("/api/queue/status");
        if (!cancelled) setQueueStatus(snap);
      } catch {
        // best-effort polling; silently ignore transient API errors
      }
    };
    void tick();
    const id = window.setInterval(() => {
      void tick();
    }, running ? 3000 : 12000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [profile, running]);

  return (
    <main className="page build-page">
      {!result && !running && (
        <PageIntro
          eyebrow="AI builder"
          icon="wand"
          title={<>Build a <span className="grad">battle-ready deck.</span></>}
          body="Describe a strategy, seed key cards, constrain the format or collection, and let Foil Bloom assemble a legal list."
        />
      )}

      {!result && !running && profile && profile.plan === "free" && (
        <div className="quota-banner">
          <span>
            Free plan: {profile.buildsToday ?? 0} / {profile.dailyCap ?? 3} builds
            used today.{" "}
            {remaining != null && remaining > 0
              ? `${remaining} remaining.`
              : "Daily cap reached - resets at UTC midnight."}
          </span>
          <Link to="/pricing">Upgrade to Pro</Link>
        </div>
      )}

      {!result && !running && error && progress.length === 0 && (
        showFloodRecovery ? (
          <BuildFloodErrorPanel
            error={error}
            onContinueAllowLandFlood={onContinueAllowLandFlood}
            onContinueOutsideCollection={onContinueOutsideCollection}
          />
        ) : (
          <div className="error-banner">{error}</div>
        )
      )}

      {!result && !running && (
        <section className="build-workspace" aria-labelledby="build-title">
          <div className="build-main-panel">
            <div className="build-panel-head">
              <span className="build-step" aria-hidden="true">
                <Icon name="wand" size={16} />
              </span>
              <div>
                <h2 id="build-title">Build setup</h2>
                <p>Choose the format, card pool, budget, and seeds before writing the brief.</p>
              </div>
            </div>

            <div className="build-settings-stack build-settings-stack--main">
              <div className="build-settings-card">
                <div className="build-section-title">
                  <Icon name="cards" size={16} />
                  <div>
                    <h3>Setup</h3>
                    <p>Format, legality, and collection pool.</p>
                  </div>
                </div>
                <div className="build-form-block">
                  <label className="field build-field--flush">
                    <span>Format</span>
                    {metaLoading ? (
                      <Skeleton variant="block" height={40} width={200} style={{ borderRadius: 10 }} />
                    ) : (
                      <Select
                        value={format}
                        onChange={setFormat}
                        minWidth="100%"
                        ariaLabel="Format"
                        options={formats.map((f) => ({ value: f.id, label: f.label }))}
                      />
                    )}
                  </label>
                </div>
                <div className="build-switches-pair">
                  <BuildSwitchRow
                    labelText="Sideboard"
                    checked={includeSideboard}
                    onChange={setIncludeSideboard}
                  >
                    Include a full 15-card matchup package when the format allows.
                  </BuildSwitchRow>
                  <BuildSwitchRow
                    labelText="Arena-legal printings"
                    checked={arenaOnly}
                    onChange={setArenaOnly}
                  >
                    Prefer card versions that exist on MTG Arena.
                  </BuildSwitchRow>
                </div>

                <div className="build-compact-divider" />
                <label className="field build-field--flush">
                  <span>Collection import to enforce</span>
                  <Select
                    value={selectedCollectionId ?? ""}
                    onChange={(v) => setSelectedCollectionId(v || null)}
                    minWidth="100%"
                    ariaLabel="Collection import"
                    options={
                      collections.length > 0
                        ? collections.map((c, idx) => ({
                            value: c.id,
                            label:
                              `${c.name?.trim() || `Import ${collections.length - idx}`}` +
                              (c.totals?.unique_cards ? ` - ${c.totals.unique_cards} unique` : ""),
                          }))
                        : [{ value: "", label: "No collections" }]
                    }
                  />
                </label>
                <BuildSwitchRow
                  labelText="Owned cards only"
                  checked={collectionOnly && !!savedCollection?.saved}
                  disabled={!savedCollection?.saved}
                  onChange={setCollectionOnly}
                  tip={
                    savedCollection?.saved
                      ? "Restrict the candidate pool to this selected collection import."
                      : "Import your Arena collection on the Collection page first, then this becomes available."
                  }
                >
                  {savedCollection?.saved ? (
                    "Build only from cards in the import above."
                  ) : (
                    <>
                      Import a collection to unlock.{" "}
                      <Link to="/collection">Open Collection</Link>
                    </>
                  )}
                </BuildSwitchRow>
              </div>

              <div className="build-settings-card">
                <div className="build-section-title">
                  <Icon name="gauge" size={16} />
                  <div>
                    <h3>Constraints</h3>
                    <p>{budgetLocked ? "Budget is locked to owned card counts." : "Wildcard caps and optional set steering."}</p>
                  </div>
                </div>
                <div className="budget-row budget-row--redo budget-row--wide">
                  <label className="field">
                    <span>Budget / wildcard preset</span>
                    <Select
                      value={budgetLocked ? "unlimited" : preset}
                      onChange={onPresetChange}
                      minWidth="100%"
                      ariaLabel="Budget preset"
                      disabled={budgetLocked}
                      options={[
                        { value: "unlimited", label: "Unlimited" },
                        { value: "budget", label: "Budget (16 rares / 4 mythics)" },
                        { value: "low", label: "Low budget (8 rares / 2 mythics)" },
                        { value: "verylow", label: "Very low (4 rares / 1 mythic)" },
                        { value: "none", label: "No rares or mythics" },
                        { value: "custom", label: "Custom..." },
                      ]}
                    />
                  </label>
                  <label className="field">
                    <span>Max rares</span>
                    <NumberStepper
                      value={budgetLocked ? "" : maxRares}
                      onChange={onManualBudget(setMaxRares)}
                      min={0}
                      max={60}
                      placeholder="No limit"
                      ariaLabel="Maximum rares"
                      disabled={budgetLocked}
                    />
                  </label>
                  <label className="field">
                    <span>Max mythics</span>
                    <NumberStepper
                      value={budgetLocked ? "" : maxMythics}
                      onChange={onManualBudget(setMaxMythics)}
                      min={0}
                      max={60}
                      placeholder="No limit"
                      ariaLabel="Maximum mythics"
                      disabled={budgetLocked}
                    />
                  </label>
                </div>

                <div className="build-compact-divider" />
                <div className="build-sets-block">
                  <label className="field sets-input-field build-field--flush">
                    <span>Featured sets</span>
                    <div className="sets-input-wrap">
                      <input
                        className="build-sets-datalist-input"
                        type="text"
                        list="build-sets-datalist"
                        placeholder="Type a set name or code, e.g. Strixhaven or STX"
                        value={setsInput}
                        onChange={(e) => onSetsInputChange(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addSet();
                          }
                        }}
                      />
                    </div>
                    <datalist id="build-sets-datalist">
                      {availableSets.map((s) => (
                        <option key={s.code} value={`${s.name} (${s.code})`} />
                      ))}
                    </datalist>
                  </label>
                  <BuildSwitchRow
                    labelText="Strictly these sets"
                    checked={onlySets}
                    onChange={setOnlySets}
                    tip="When on, only non-basic cards from the featured sets above are allowed (basics are still added as needed)."
                  >
                    Tighten the pool so non-land cards must come from the featured list.
                  </BuildSwitchRow>
                </div>
                {selectedSets.length > 0 && (
                  <div className="sets-chips">
                    {selectedSets.map((s) => (
                      <span key={s.code} className="set-chip">
                        <span className="code">{s.code}</span>
                        <span className="name">{s.name}</span>
                        <button
                          type="button"
                          aria-label={`Remove ${s.name}`}
                          onClick={() =>
                            setSelectedSets((xs) => xs.filter((x) => x.code !== s.code))
                          }
                        >
                          X
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="build-section">
              <div className="build-section-title">
                <Icon name="sparkles" size={16} />
                <div>
                  <h3>Deck brief</h3>
                  <p>Optional anchor cards and prompt. {aroundCards.length} / {AROUND_CARDS_MAX} cards selected.</p>
                </div>
              </div>
              <label className="field around-card-field">
                <CardPicker
                  multi
                  values={aroundCards}
                  onAdd={(card) =>
                    setAroundCards((xs) =>
                      xs.some((x) => x.oracle_id === card.oracle_id)
                        ? xs
                        : [...xs, card],
                    )
                  }
                  onRemove={(oracleId) =>
                    setAroundCards((xs) => xs.filter((x) => x.oracle_id !== oracleId))
                  }
                  arenaOnly={arenaOnly}
                  placeholder="e.g. Sheoldred, the Apocalypse"
                  maxItems={AROUND_CARDS_MAX}
                />
              </label>

              <div className="build-compact-divider" />
              <label className="field build-prompt-field build-prompt-field--hero">
                <span>Describe the deck you want</span>
                <textarea
                  rows={6}
                  placeholder="Leave blank to generate a strong list for the format and card pool, or describe a plan like aggressive red-white counters with prowess."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </label>
              <div className="build-quick-prompts" aria-label="Quick deck prompts">
                <button
                  type="button"
                  className="quick-prompt-card quick-prompt-card--gold"
                  onClick={() => setQuery("Build a resilient midrange deck with efficient removal, card advantage, and a clean late-game finisher.")}
                >
                  <Icon name="shield" size={17} />
                  <span>Resilient midrange</span>
                </button>
                <button
                  type="button"
                  className="quick-prompt-card quick-prompt-card--rose"
                  onClick={() => setQuery("Build a fast aggressive deck that curves out, attacks early, and can finish through blockers.")}
                >
                  <Icon name="bolt" size={17} />
                  <span>Fast aggro</span>
                </button>
                <button
                  type="button"
                  className="quick-prompt-card quick-prompt-card--cool"
                  onClick={() => setQuery("Build a tempo deck with cheap threats, protection, interaction, and strong card selection.")}
                >
                  <Icon name="wand" size={17} />
                  <span>Tempo spells</span>
                </button>
              </div>
            </div>

            <details className="arena-quest-suggestions" aria-label="MTG Arena daily quest deck suggestions">
              <summary>
                <span>Prompt ideas</span>
                <small>Tap an Arena-style objective to append a matching goal.</small>
              </summary>
              <div className="arena-quest-chips">
                {ARENA_QUEST_SUGGESTIONS.map((suggestion) => (
                  <button
                    type="button"
                    key={suggestion.quest}
                    className="arena-quest-chip"
                    onClick={() => addQuestSuggestion(suggestion)}
                  >
                    {suggestion.quest}
                  </button>
                ))}
              </div>
            </details>
          </div>

          <aside className="build-side-panel" aria-label="Deck builder summary">
            <div className="build-summary-card">
              <div className="build-summary-orbit" aria-hidden="true" />
              <span className="eyebrow build-summary-eyebrow">
                <span className="dot" />
                <Icon name="magic" size={14} />
                Build loadout
              </span>
              <div className="build-summary-grid">
                {buildSummaryItems.map((item) => (
                  <div key={item.label} className="build-summary-item">
                    <Icon name={item.icon} size={16} />
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>
              <div className={`build-readiness ${hasBrief ? "is-ready" : ""}`}>
                <span className="build-readiness-dot" />
                {hasBrief ? "Brief ready" : "Open-ended build"}
              </div>
              {showQueueInSidebar && queueHint && (
                <p className="build-queue-hint" role="status">
                  {queueHint}
                </p>
              )}
            </div>

            <div className="build-side-note">
              <Icon name="rocket" size={18} />
              <p>Generate uses every setting in order, then streams progress below this form.</p>
            </div>

            <div className="actions build-actions build-actions--sticky">
              <button type="button" className="primary build-generate-btn" onClick={onBuild} disabled={running || capWarning}>
                {running ? (
                  <>
                    <Spinner className="button-spinner" />
                    Generating...
                  </>
                ) : (
                  <><Icon name="sparkles" size={16} />Generate Deck</>
                )}
              </button>
              <button className="secondary" onClick={cancelBuild} disabled={!running}>
                Cancel
              </button>
            </div>
          </aside>
        </section>
      )}

      {!result && (running || progress.length > 0) && (
        <div className="progress build-progress-focus" aria-live="polite" aria-busy={running}>
          {showQueueInProgress && queueHint && (
            <p className="build-queue-hint build-queue-hint--tight" role="status">
              {queueHint}
            </p>
          )}
          <ul>
            {running && progress.length === 0 && (
              <li className="active">Starting…</li>
            )}
            {progress.map((p, i) => (
              <li
                key={i}
                className={
                  p.kind === "active"
                    ? "active"
                    : p.kind === "done"
                    ? "done"
                    : p.kind === "error"
                    ? "progress-line-error"
                    : undefined
                }
              >
                {p.text}
              </li>
            ))}
          </ul>
          {error && (
            showFloodRecovery ? (
              <BuildFloodErrorPanel
                error={error}
                onContinueAllowLandFlood={onContinueAllowLandFlood}
                onContinueOutsideCollection={onContinueOutsideCollection}
              />
            ) : (
              <div className="error-banner">{error}</div>
            )
          )}
        </div>
      )}

      {result && error && <div className="error-banner">{error}</div>}

      {result && queueHint && (
        <p className="build-queue-hint build-queue-hint--result" role="status">
          {queueHint}
        </p>
      )}

      {result && (
        <BuildResultView
          result={result}
          queryText={query.trim()}
          format={format}
          onNewDeck={() => {
            setResult(null);
            setProgress([]);
            setError(null);
            setErrorCode(null);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Result rendering
// ---------------------------------------------------------------------------

function BuildResultView({
  result,
  queryText,
  format,
  onNewDeck,
}: {
  result: BuildResult;
  queryText: string;
  format: string;
  onNewDeck: () => void;
}) {
  const t = result.theme ?? {};
  const totals = result.totals ?? { mainboard: 0, sideboard: 0 };
  const rc = result.rarity_counts ?? {};
  const caps = result.caps ?? {};
  const rareText =
    caps.max_rares != null
      ? `Rares: ${rc.rare || 0} / ${caps.max_rares}`
      : `Rares: ${rc.rare || 0}`;
  const mythicText =
    caps.max_mythics != null
      ? `Mythics: ${rc.mythic || 0} / ${caps.max_mythics}`
      : `Mythics: ${rc.mythic || 0}`;
  const sets = result.sets ?? t.resolved_sets ?? [];
  const conformance = result.set_conformance ?? {
    in_set: 0,
    total_non_basic: 0,
    ratio: 0,
  };

  const defaultName =
    (result.name && result.name.trim()) ||
    buildDefaultName(queryText, t.archetype, t.colors);
  const [saveName, setSaveName] = useState(defaultName);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [publishOnSave, setPublishOnSave] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "copied">("idle");
  const copyToastTimer = useRef<number | null>(null);

  useEffect(() => {
    setSaveName(defaultName);
  }, [defaultName]);

  useEffect(() => {
    return () => {
      if (copyToastTimer.current != null) {
        window.clearTimeout(copyToastTimer.current);
      }
    };
  }, []);

  const copyArena = async () => {
    if (!result.arena_text) return;
    try {
      await navigator.clipboard.writeText(result.arena_text);
      setCopyState("copied");
      if (copyToastTimer.current != null) {
        window.clearTimeout(copyToastTimer.current);
      }
      copyToastTimer.current = window.setTimeout(() => {
        setCopyState("idle");
        copyToastTimer.current = null;
      }, 1600);
    } catch (e) {
      alert("Copy failed: " + (e as Error).message);
    }
  };

  const onSave = async () => {
    setSaveError(null);
    const trimmed = saveName.trim() || defaultName;
    setSaveState("saving");
    try {
      const saved = await apiPost<SavedDeckSummary>("/api/decks", {
        name: trimmed,
        format,
        query: queryText,
        published: publishOnSave,
        deck: result.deck,
        arena_text: result.arena_text,
        rationale: result.rationale,
        theme: result.theme,
        scenarios: result.scenarios,
        totals: result.totals,
        rarity_counts: result.rarity_counts,
        caps: result.caps,
        sets: result.sets ?? t.resolved_sets ?? [],
        set_conformance: result.set_conformance ?? {},
        notes: result.notes,
      });
      setSavedId(saved.id);
      setSaveState("saved");
    } catch (e) {
      setSaveError((e as Error).message);
      setSaveState("error");
    }
  };

  return (
    <div className="result">
      <div className="result-head">
        <div>
          <h2>Result</h2>
          <label className="result-deck-name">
            <span className="muted">Deck name</span>
            <input
              type="text"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              disabled={saveState === "saving" || saveState === "saved"}
              autoComplete="off"
              name="saved-deck-name"
              title="Name used when you save, publish, and copy exports"
            />
          </label>
        </div>
        <div className="result-actions">
          <label className="publish-save-toggle">
            <input
              type="checkbox"
              checked={publishOnSave}
              onChange={(e) => setPublishOnSave(e.target.checked)}
              disabled={saveState === "saving" || saveState === "saved"}
            />
            <span>Publish</span>
          </label>
          <button
            type="button"
            className="primary"
            onClick={onSave}
            disabled={saveState === "saving"}
          >
            {saveState === "saving" ? (
              <>
                <Spinner className="button-spinner" />
                Saving...
              </>
            ) : saveState === "saved" ? (
              "Saved ✓"
            ) : (
              "Save deck"
            )}
          </button>
          <button
            className="secondary"
            onClick={onNewDeck}
            disabled={saveState === "saving"}
            title="Discard this deck and build a new one"
          >
            New deck
          </button>
          {saveState === "saved" && savedId && (
            <Link to="/decks" className="topbar-btn">View saved</Link>
          )}
          {saveState === "saved" && publishOnSave && (
            <Link to="/community" className="topbar-btn">Community</Link>
          )}
        </div>
      </div>
      {saveError && <div className="error-banner">{saveError}</div>}

      {result.collection_build && (
        <div
          className={`collection-banner collection-banner-${result.collection_build.quality}`}
          role="status"
        >
          <div className="collection-banner-title">
            {(result.collection_build.outside_collection_copies ?? 0) > 0
              ? "Completed with outside cards"
              : result.collection_build.quality === "good"
              ? "Built from your collection"
              : result.collection_build.quality === "tight"
              ? "Built from your collection (tight fit)"
              : "Built from your collection — best effort"}
          </div>
          <div className="collection-banner-body">
            {result.collection_build.message}
          </div>
          <div className="collection-banner-stats">
            <span className="pill">
              Owned cards used: {result.collection_build.owned_copies_used}
              {" "}({result.collection_build.owned_unique_used} unique)
            </span>
            {(result.collection_build.outside_collection_copies ?? 0) > 0 && (
              <span className="pill">
                Outside collection: {result.collection_build.outside_collection_copies}
                {" "}({result.collection_build.outside_collection_unique ?? 0} unique)
              </span>
            )}
            {result.collection_build.extra_basics_added > 0 && (
              <span className="pill">
                Extra basic lands: {result.collection_build.extra_basics_added}
              </span>
            )}
            <span className="pill">
              Pool size: {result.collection_build.owned_unique_in_pool}
            </span>
          </div>
        </div>
      )}

      <div className="summary">
        <div className="totals">
          <span className="pill">Main: {totals.mainboard}</span>
          {totals.sideboard ? (
            <span className="pill">Side: {totals.sideboard}</span>
          ) : null}
          <span className="pill pill-rare">{rareText}</span>
          <span className="pill pill-mythic">{mythicText}</span>
        </div>
        <div className="theme-block">
          <div>
            <strong>Archetype:</strong> {t.archetype || ""}
            &nbsp;·&nbsp;
            <strong>Colors:</strong> {(t.colors || []).join("") || "-"}
          </div>
          <div style={{ marginTop: 4 }}>
            {(t.themes || []).map((tag) => (
              <span key={tag} className="tag">{tag}</span>
            ))}
          </div>
          {sets.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <strong>Featured sets:</strong>{" "}
              {sets.map((s) => (
                <span key={s.code} className="set-chip small">
                  <span className="code">{s.code}</span>
                  <span className="name">{s.name}</span>
                </span>
              ))}
              {conformance.total_non_basic > 0 && (
                <span className="muted">
                  {" "}
                  - {conformance.in_set} / {conformance.total_non_basic} non-basic
                  cards ({Math.round((conformance.ratio || 0) * 100)}%)
                </span>
              )}
            </div>
          )}
          {t.notes && (
            <div style={{ marginTop: 4, fontStyle: "italic" }}>{t.notes}</div>
          )}
        </div>
      </div>

      {result.mission && (
        <div className="mission-block">
          <h3>Mission</h3>
          <p>{result.mission}</p>
        </div>
      )}

      {result.rationale && (
        <div className="rationale-block">
          <h3>Rationale</h3>
          <p>{result.rationale}</p>
        </div>
      )}

      {result.pro_audit ? (
        <div className="build-result-layout">
          <div className="build-result-primary">
            <div className="deck-view">
              <h3>Decklist</h3>
              <DeckSections deck={result.deck} />
            </div>
          </div>
          <aside className="build-result-audit" aria-label="Pro-build audit">
            <ProAuditView stats={result.pro_audit} className="pro-audit--build" />
          </aside>
        </div>
      ) : (
        <div className="deck-view">
          <h3>Decklist</h3>
          <DeckSections deck={result.deck} />
        </div>
      )}

      <div className="arena-block">
        <div className="arena-header">
          <h3>MTG Arena import text</h3>
          <div className="copy-toast-wrap">
            <button className="secondary" onClick={copyArena}>Copy to clipboard</button>
            {copyState === "copied" && (
              <span className="copy-toast" role="status" aria-live="polite">
                Copied
              </span>
            )}
          </div>
        </div>
        <pre className="arena-text" spellCheck={false}>{result.arena_text ?? ""}</pre>
      </div>

      <ScenariosView scenarios={result.scenarios} />

      {(result.notes || []).length > 0 && (
        <details className="notes-details">
          <summary>
            <span>{result.notes!.length}</span> build adjustments applied (click to expand)
          </summary>
          <ul>
            {result.notes!.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
}

function DeckSections({ deck }: { deck: DeckShape }) {
  const order: (keyof DeckShape)[] = ["mainboard", "commander", "companion", "sideboard"];
  const typeRank = (type: string): number => {
    if (type.includes("Creature")) return 1;
    if (type.includes("Planeswalker") || type.includes("Battle")) return 2;
    if (type.includes("Instant") || type.includes("Sorcery")) return 3;
    if (type.includes("Enchantment")) return 4;
    if (type.includes("Artifact")) return 5;
    if (type.includes("Land")) return 7;
    return 6;
  };
  const [viewMode, setViewMode] = useState<"list" | "gallery">("list");

  return (
    <>
      <div className="deck-view-toolbar">
        <div
          className="deck-view-modes"
          role="tablist"
          aria-label="Deck view mode"
        >
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "list"}
            className={viewMode === "list" ? "active" : undefined}
            onClick={() => setViewMode("list")}
          >
            List
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "gallery"}
            className={viewMode === "gallery" ? "active" : undefined}
            onClick={() => setViewMode("gallery")}
          >
            Images
          </button>
        </div>
      </div>
      {order.map((key) => {
        const entries = (deck[key] || []).filter((e) => e.count > 0);
        if (!entries.length) return null;
        const sorted = [...entries].sort((a, b) => {
          const ra = typeRank(a.card?.type_line ?? "");
          const rb = typeRank(b.card?.type_line ?? "");
          if (ra !== rb) return ra - rb;
          const ca = a.card?.cmc ?? 0;
          const cb = b.card?.cmc ?? 0;
          if (ca !== cb) return ca - cb;
          return a.name.localeCompare(b.name);
        });
        const total = sorted.reduce((acc, e) => acc + e.count, 0);
        const title = `${key.charAt(0).toUpperCase() + key.slice(1)} (${total})`;
        if (viewMode === "gallery") {
          return (
            <div key={key} className="deck-section">
              <h4>{title}</h4>
              <div className="deck-gallery">
                {sorted.map((e, i) => (
                  <div className="deck-gallery-item" key={`${e.name}-${i}`}>
                    <CardThumb
                      card={{
                        name: e.name,
                        image_url: e.card?.image_url,
                        type_line: e.card?.type_line,
                      }}
                      size="md"
                    />
                    <div className="deck-gallery-caption">
                      <span className="qty">{e.count}×</span>
                      <span className="name">{e.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        }
        return (
          <div key={key} className="deck-section">
            <h4>{title}</h4>
            <ul>
              {sorted.map((e, i) => {
                const meta = e.card?.mana_cost || e.card?.type_line || "";
                return (
                  <li key={`${e.name}-${i}`}>
                    <span className="qty">{e.count}</span>
                    <CardHover
                      card={{
                        name: e.name,
                        image_url: e.card?.image_url,
                        type_line: e.card?.type_line,
                        mana_cost: e.card?.mana_cost,
                      }}
                      className="name"
                    >
                      {e.name}
                    </CardHover>
                    <span className="meta">{meta}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </>
  );
}

function ScenariosView({ scenarios }: { scenarios?: Scenarios }) {
  if (!scenarios || !scenarios.opening_hand) return null;
  return (
    <div className="scenarios-block">
      <h3>How to play this deck</h3>
      <div className="scenario-grid">
        <div className="scenario-card">
          <h4>Opening hand</h4>
          <p>{scenarios.opening_hand}</p>
        </div>
        <div className="scenario-card">
          <h4>Key interactions</h4>
          <ul>
            {(scenarios.key_interactions || []).map((txt, i) => (
              <li key={i}>{txt}</li>
            ))}
          </ul>
        </div>
      </div>

      {(scenarios.game_plan || []).length > 0 && (
        <>
          <h4 className="section-sub">Turn-by-turn plan</h4>
          <div className="scenario-plan">
            {(scenarios.game_plan || []).map((p, i) => (
              <div key={i} className="plan-card">
                <div className="plan-phase">{p.phase}</div>
                <div className="plan-text">{p.plan}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {(scenarios.matchups || []).length > 0 && (
        <>
          <h4 className="section-sub">Matchup notes</h4>
          <div className="matchup-grid">
            {(scenarios.matchups || []).map((m, i) => (
              <div key={i} className="matchup-card">
                <div className="matchup-against">vs {m.against}</div>
                <div className="matchup-strategy">{m.strategy}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {(scenarios.win_conditions || []).length > 0 && (
        <>
          <h4 className="section-sub">Win conditions</h4>
          <ul>
            {(scenarios.win_conditions || []).map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </>
      )}
    </div>
  );
}

function buildDefaultName(
  query: string,
  archetype?: string,
  colors?: string[],
): string {
  const pieces: string[] = [];
  if (archetype) pieces.push(archetype);
  if (colors && colors.length) pieces.push(colors.join(""));
  if (pieces.length) return pieces.join(" · ");
  const words = (query || "").split(/\s+/).filter(Boolean).slice(0, 6);
  if (words.length) return words.join(" ");
  return "Untitled deck";
}
